import React, { Fragment, useMemo } from 'react';
import { styled } from 'dripsy';
import { View } from 'react-native';
import { useTranslation } from 'react-i18next';

import { ErrorResultSvg } from '../svg/ErrorResultSvg';
import { NoResultSvg } from '../svg/NoResultSvg';
import { BaseText } from '../common/BaseText';
import { Separator } from '../common/Separator';
import { Button } from '../common/Button';
import { NoSavedVideosSvg } from '../svg/NoSavedVideosSvg';
import { NoFriendsSvg } from '../svg/NoFriendsSvg';
import { NoUserPostsSvg } from '../svg/NoUserPostsSvg';
import { NoNotificationsSvg } from '../svg/NoNotificationsSvg';
import { NoMessagesSvg } from '../svg/NoMessagesSvg';
import { NoPostsSvg } from '../svg/NoPostsSvg';
import { NoSearchStartedSvg } from '../svg/NoSearchStartedSvg';
import { NoTrackFeedbackSvg } from '../svg/NoTrackFeedbackSvg';
import { SubscribeSuggestionSvg } from '../svg/SubscribeSuggestionSvg';
import { NoMatchingResultSvg } from '../svg/NoMatchingResultSvg';
import { SectionItemListTypeEnum } from '@constants';

const Container = styled(View)({
  maxWidth: [null, 420],
  alignItems: 'center',
  justifyContent: 'center',
  alignSelf: [null, 'center'],
  flex: 1,
});

const Title = styled(BaseText)({
  color: '$heading800',
  variant: 'text.xlarge',
  fontWeight: 'semi',
  textAlign: 'center',
});

const Description = styled(BaseText)({
  color: '$text400',
  variant: 'text.medium',
  textAlign: 'center',
});

const ButtonContainer = styled(View)({
  width: ['100%', 165],
});

export enum EmptyResultTypeEnum {
  Error = 'error',
  NoResult = 'no-result',
  NoSavedVideos = 'no-saved-videos',
  NoFollowers = 'no-followers',
  NoFollowing = 'no-following',
  NoFriends = 'no-friends',
  NoUserPosts = 'no-user-posts',
  NoNotifications = 'no-notifications',
  NoMessages = 'no-messages',
  NoPosts = 'no-posts',
  NoSearchStarted = 'no-search-started',
  NoTrackFeedback = 'no-track-feedback',
  NoAccessForTrialPlan = 'no-access-for-trial-plan',
  NoHistory = 'no-history',
}

function getTypeByItemListType(type: SectionItemListTypeEnum) {
  switch (type) {
    case SectionItemListTypeEnum.History:
      return EmptyResultTypeEnum.NoHistory;
    case SectionItemListTypeEnum.Saved:
      return EmptyResultTypeEnum.NoSavedVideos;
    default:
      return EmptyResultTypeEnum.NoResult;
  }
}

function getSvgByType(type?: EmptyResultTypeEnum) {
  switch (type) {
    case EmptyResultTypeEnum.Error:
      return ErrorResultSvg;
    case EmptyResultTypeEnum.NoResult:
      return NoResultSvg;
    case EmptyResultTypeEnum.NoSavedVideos:
      return NoSavedVideosSvg;
    case EmptyResultTypeEnum.NoFriends:
    case EmptyResultTypeEnum.NoFollowers:
    case EmptyResultTypeEnum.NoFollowing:
      return NoFriendsSvg;
    case EmptyResultTypeEnum.NoUserPosts:
      return NoUserPostsSvg;
    case EmptyResultTypeEnum.NoNotifications:
      return NoNotificationsSvg;
    case EmptyResultTypeEnum.NoMessages:
      return NoMessagesSvg;
    case EmptyResultTypeEnum.NoPosts:
      return NoPostsSvg;
    case EmptyResultTypeEnum.NoSearchStarted:
      return NoSearchStartedSvg;
    case EmptyResultTypeEnum.NoTrackFeedback:
      return NoTrackFeedbackSvg;
    case EmptyResultTypeEnum.NoAccessForTrialPlan:
      return SubscribeSuggestionSvg;
    case EmptyResultTypeEnum.NoHistory:
      return NoMatchingResultSvg;

    default:
      return ErrorResultSvg;
  }
}

function getKeyByType(type?: EmptyResultTypeEnum) {
  switch (type) {
    case EmptyResultTypeEnum.Error:
      return {
        titleKey: 'common.emptyResult.errorDefaultTitle',
      };
    case EmptyResultTypeEnum.NoResult:
      return {
        titleKey: 'common.emptyResult.noResultFoundDefaultTitle',
      };
    case EmptyResultTypeEnum.NoSavedVideos:
      return {
        titleKey: 'common.emptyResult.noSavedVideosTitle',
        descriptionKey: 'common.emptyResult.noSavedVideosDescription',
      };
    case EmptyResultTypeEnum.NoFollowers:
      return {
        titleKey: 'common.emptyResult.noFollowersTitle',
      };
    case EmptyResultTypeEnum.NoFollowing:
      return {
        titleKey: 'common.emptyResult.noFollowingTitle',
      };
    case EmptyResultTypeEnum.NoFriends:
      return {
        titleKey: 'common.emptyResult.noFriendsTitle',
        descriptionKey: 'common.emptyResult.noFriendsDescription',
        buttonTitleKey: 'common.emptyResult.noFriendsButtonTitle',
      };
    case EmptyResultTypeEnum.NoUserPosts:
      return {
        titleKey: 'common.emptyResult.noUserPostsTitle',
      };
    case EmptyResultTypeEnum.NoNotifications:
      return {
        titleKey: 'common.emptyResult.noNotificationsTitle',
        descriptionKey:
          'common.emptyResult.noNotificationsDescription',
      };
    case EmptyResultTypeEnum.NoMessages:
      return {
        titleKey: 'common.emptyResult.noMessagesTitle',
        descriptionKey: 'common.emptyResult.noMessagesDescription',
      };
    case EmptyResultTypeEnum.NoPosts:
      return {
        titleKey: 'common.emptyResult.noPostsTitle',
        descriptionKey: 'common.emptyResult.noPostsDescription',
      };
    case EmptyResultTypeEnum.NoSearchStarted:
      return {
        titleKey: 'common.emptyResult.noSearchStarted',
      };
    case EmptyResultTypeEnum.NoTrackFeedback:
      return {
        titleKey: 'common.emptyResult.noTrackFeedbackTitle',
      };
    case EmptyResultTypeEnum.NoAccessForTrialPlan:
      return {
        titleKey: 'common.emptyResult.noAccessForTrialPlanTitle',
        descriptionKey:
          'common.emptyResult.noAccessForTrialPlanDescription',
        buttonTitleKey:
          'common.emptyResult.noAccessForTrialPlanButtonTitle',
      };
    case EmptyResultTypeEnum.NoHistory:
      return {
        titleKey: 'common.emptyResult.noHistoryTitle',
        descriptionKey: 'common.emptyResult.noHistoryDescription',
      };

    default:
      return {
        titleKey: 'common.emptyResult.errorDefaultTitle',
      };
  }
}

function getIconByType(type?: EmptyResultTypeEnum) {
  switch (type) {
    case EmptyResultTypeEnum.Error:
      return '🥱';
    case EmptyResultTypeEnum.NoResult:
      return '🤷‍♂️';
    case EmptyResultTypeEnum.NoSavedVideos:
      return '🤔';
    case EmptyResultTypeEnum.NoFriends:
      return '🥺';
    case EmptyResultTypeEnum.NoUserPosts:
      return '🤷‍♀️';
    case EmptyResultTypeEnum.NoSearchStarted:
      return '🤗';
    case EmptyResultTypeEnum.NoNotifications:
      return '☘️';
    case EmptyResultTypeEnum.NoMessages:
      return '🤐';
    case EmptyResultTypeEnum.NoPosts:
    case EmptyResultTypeEnum.NoHistory:
      return '😴';
    case EmptyResultTypeEnum.NoTrackFeedback:
      return '🤔';
    case EmptyResultTypeEnum.NoAccessForTrialPlan:
      return '🤩';

    default:
      return '🥱';
  }
}

export const emptyResult = {
  getSvgByType,
  getKeyByType,
  getIconByType,
  getTypeByItemListType,
};

export interface IEmptyResultView {
  title?: string;
  description?: string;
  buttonTitle?: string;
  type?: EmptyResultTypeEnum;
  onPress?(): void;
}

export function EmptyResultView(props: IEmptyResultView) {
  const { t } = useTranslation();

  const { titleKey, descriptionKey, buttonTitleKey } = getKeyByType(
    props.type,
  );

  const title = props.title ?? t(titleKey);
  const description =
    props.description ?? (descriptionKey && t(descriptionKey));
  const buttonTitle =
    props.buttonTitle ?? (buttonTitleKey && t(buttonTitleKey));

  const SvgComponent = useMemo(
    () => getSvgByType(props.type),
    [props.type],
  );

  return (
    <Container>
      <SvgComponent />
      <Separator size="$9" />
      <Title>
        {title} {getIconByType(props.type)}
      </Title>
      <Separator size="$4" />
      {!!description && <Description>{description}</Description>}
      {!!buttonTitle && (
        <Fragment>
          <Separator size={['$13', '$7']} />
          <ButtonContainer>
            <Button onPress={props.onPress}>{buttonTitle}</Button>
          </ButtonContainer>
        </Fragment>
      )}
    </Container>
  );
}
